import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { TextField } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TextInputField from "../../form/TextInputField";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import "./CreateContactModal.css";

import {
  type DetailedStage,
  type NextAction
} from "../../../config/stageConfig";
import { useLeadStages } from "../../../hooks/useLeadStages";

interface CreateContactModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  leadId?: string;
  /**
   * Current detailed stage of the lead (if available).
   * Used to control which "Next Action" options are shown.
   */
  currentStage?: DetailedStage;
  /**
   * Current stage sub-status (if available).
   * Used to determine if stages are completed.
   */
  currentStageSubStatus?: string;
  /**
   * Follow-up history of the lead (if available).
   * Used to check if flexible stages have been completed.
   */
  followUpHistory?: Array<{
    type?: 'phone' | 'whatsapp' | 'email' | 'siteVisit' | 'storeVisit' | 'followup';
    channel?: string;
    date: string;
    outcome?: string;
  }>;
  /**
   * Completed stages with their sub-status (if available).
   * Used to accurately determine which stages are completed.
   */
  completedStages?: Partial<Record<'storeVisit' | 'siteVisit' | 'designFinalized' | 'measurement' | 'quotation', 'scheduled' | 'completed' | 'send' | 'approved'>>;
  /**
   * Optional defaults used when opening the modal from a specific
   * store visit / site visit context (e.g. reschedule from timeline).
   */
  initialInteractionType?: "postUpdate" | "storeVisit" | "siteVisit";
  initialNextAction?: NextAction;
  /**
   * When true, the modal is opened in reschedule mode for store/site visit.
   * This changes the title and pre-selects the appropriate action.
   */
  rescheduleMode?: boolean;
  onSuccess?: (payload: {
    followUpEntry: {
      id: string;
      type:
      | "phone"
      | "whatsapp"
      | "email"
      | "siteVisit"
      | "storeVisit"
      | "followup";
      channel: string;
      date: string;
      summary: string;
      outcome: string;
      outcomeColor: "success" | "info" | "warning" | "error";
      owner: string;
      ownerInitials: string;
    };
    stageUpdate?: {
      detailedStage: DetailedStage;
      stageSubStatus?: "scheduled" | "completed" | "send" | "approved";
    };
  }) => void;
}

type ContactType = "business" | "individual";

interface FormData {
  type: ContactType;
  // Business fields
  organizerName: string;
  businessName: string;
  designation: string;
  decisionMaker: "yes" | "no" | "";
  // Individual fields
  name: string;
  // Common fields
  email: string;
  mobile: string;
  // Interaction / follow-up fields
  interactionType: "postUpdate" | "storeVisit" | "siteVisit" | "";
  contactMethod: ContactMethodValue | "";
  interactionNote: string;
  // Next action also encodes stage updates
  nextAction: NextActionForm;
  interactionDate: string;
  interactionTime: string;
  googleMapsUrl: string;
}

// Reuse the union from FormData for convenience
// Note: NextAction type is now imported from stageConfig
type NextActionForm = NextAction | "";

// Contact method options: single source of truth for options and display order.
// Reorder or add/remove entries here to update the form and sequence.
const CONTACT_METHODS = [
  { value: "phone", label: "Phone" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "mail", label: "Mail" },
] as const;

type ContactMethodValue = (typeof CONTACT_METHODS)[number]["value"];

interface FormErrors {
  type?: string;
  organizerName?: string;
  businessName?: string;
  designation?: string;
  name?: string;
  email?: string;
  mobile?: string;
  interactionType?: string;
  contactMethod?: string;
  interactionNote?: string;
  nextAction?: string;
  interactionDate?: string;
  interactionTime?: string;
  googleMapsUrl?: string;
  submit?: string;
}

const CreateContactModal = ({
  isOpen,
  onRequestClose,
  leadId,
  currentStage,
  currentStageSubStatus,
  followUpHistory,
  completedStages: leadCompletedStages,
  initialInteractionType,
  initialNextAction,
  rescheduleMode,
  onSuccess,
}: CreateContactModalProps) => {
  const {
    getStageByKey,
    terminalStages,
    isTerminal,
    isOptional,
    getStageStatus: getHookStageStatus,
    stages: allDynamicStages
  } = useLeadStages();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [quotationFile, setQuotationFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<FormData>({
    type: "individual",
    organizerName: "",
    businessName: "",
    designation: "",
    decisionMaker: "",
    name: "",
    email: "",
    mobile: "",
    interactionType: "",
    contactMethod: "phone",
    interactionNote: "",
    nextAction: "",
    interactionDate: "",
    interactionTime: "",
    googleMapsUrl: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [isCallLaterTimePickerOpen, setIsCallLaterTimePickerOpen] = useState(false);
  const [isVisitTimePickerOpen, setIsVisitTimePickerOpen] = useState(false);

  console.log("formData", isCallLaterTimePickerOpen, isVisitTimePickerOpen);
  console.log("quotationFile", quotationFile);

  // Convert time string (HH:mm) to Date object for TimePicker
  const timeStringToDate = (timeStr: string): Date | null => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // Convert Date object to time string (HH:mm) for form data
  const dateToTimeString = (value: any): string => {
    if (!value) return "";
    const date = value instanceof Date ? value : (typeof value?.toDate === "function" ? value.toDate() : null);
    if (!date) return "";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const dateStringToDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const dateToDateString = (value: any): string => {
    if (!value) return "";
    const date = value instanceof Date ? value : (typeof value?.toDate === "function" ? value.toDate() : null);
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Minimum date for date inputs: today (no past dates)
  const minDate = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, [isOpen]);

  // Time restrictions: 7 AM to 9 PM
  const minTime = useMemo(() => {
    const d = new Date();
    d.setHours(7, 0, 0, 0);
    return d;
  }, []);

  const maxTime = useMemo(() => {
    const d = new Date();
    d.setHours(21, 0, 0, 0);
    return d;
  }, []);

  const TIME_MINUTE_STEP = 15;

  const isWithinTimeRange = (date: Date): boolean => {
    const selectedMinutes = date.getHours() * 60 + date.getMinutes();
    const minMinutes = minTime.getHours() * 60 + minTime.getMinutes();
    const maxMinutes = maxTime.getHours() * 60 + maxTime.getMinutes();
    return selectedMinutes >= minMinutes && selectedMinutes <= maxMinutes;
  };

  const toDateValue = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === "function") return value.toDate();
    return null;
  };

  // const handleInteractionTimeChange = (
  //   newValue: any,
  //   closePicker?: () => void,
  // ) => {
  //   const parsedDate = toDateValue(newValue);

  //   // if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
  //   //   setFormData((prev) => ({
  //   //     ...prev,
  //   //     interactionTime: "",
  //   //   }));
  //   //   return;
  //   // }
  //   if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
  //     return; // don't reset while typing
  //   }

  //   if (parsedDate.getMinutes() % TIME_MINUTE_STEP !== 0) {
  //     setErrors((prev) => ({
  //       ...prev,
  //       interactionTime: `Minutes must be in ${TIME_MINUTE_STEP}-minute steps (00, 15, 30, 45)`,
  //     }));
  //     return;
  //   }

  //   if (!isWithinTimeRange(parsedDate)) {
  //     setErrors((prev) => ({
  //       ...prev,
  //       interactionTime: "Time must be between 07:00 and 21:00",
  //     }));
  //     return;
  //   }

  //   const timeStr = dateToTimeString(parsedDate);
  //   setFormData((prev) => ({
  //     ...prev,
  //     interactionTime: timeStr,
  //   }));
  //   if (errors.interactionTime) {
  //     setErrors((prev) => ({
  //       ...prev,
  //       interactionTime: undefined,
  //     }));
  //   }
  //   closePicker?.();
  // };

  const handleInteractionTimeChange = (
    newValue: any,
    closePicker?: () => void
  ) => {
    const parsedDate = toDateValue(newValue);
  
    if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
      return; // allow typing
    }
  
    const minutesValid =
      parsedDate.getMinutes() % TIME_MINUTE_STEP === 0;
  
    const rangeValid = isWithinTimeRange(parsedDate);
  
    // Always set value first ✅
    const timeStr = dateToTimeString(parsedDate);
    setFormData((prev) => ({
      ...prev,
      interactionTime: timeStr,
    }));
  
    // Then validate
    if (!minutesValid) {
      setErrors((prev) => ({
        ...prev,
        interactionTime: `Minutes must be in ${TIME_MINUTE_STEP}-minute steps (00, 15, 30, 45)`,
      }));
      return;
    }
  
    if (!rangeValid) {
      setErrors((prev) => ({
        ...prev,
        interactionTime: "Time must be between 07:00 and 21:00",
      }));
      return;
    }
  
    // clear error if valid
    setErrors((prev) => ({
      ...prev,
      interactionTime: undefined,
    }));
  
    closePicker?.();
  };

  const isDateFieldShown =
    (formData.nextAction === "callLater" ||
      formData.nextAction === "storeVisit" ||
      formData.nextAction === "siteVisit" ||
      (formData.interactionType === "postUpdate" && formData.contactMethod === "phone")) &&
    formData.nextAction !== "notInterested";

  // When Date field is shown, focus it and open the date picker
  useEffect(() => {
    if (!isOpen || !isDateFieldShown) return;
    const timer = setTimeout(() => {
      const el = dateInputRef.current;
      if (el) {
        el.focus();
        if (typeof (el as HTMLInputElement & { showPicker?: () => void }).showPicker === "function") {
          (el as HTMLInputElement & { showPicker: () => void }).showPicker();
        }
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [isOpen, isDateFieldShown]);

  // When opening the modal in a specific context (e.g. reschedule a visit)
  // and there is no existing unsaved data, preselect the interaction type
  // and next action based on the provided defaults.
  useEffect(() => {
    if (!isOpen) return;
    if (!initialInteractionType && !initialNextAction) return;

    // In reschedule mode, always set the initial values
    // Otherwise, avoid overriding user input if they already started filling the form
    if (!rescheduleMode && hasFormData()) return;

    setFormData((prev) => ({
      ...prev,
      interactionType: initialInteractionType || prev.interactionType,
      nextAction: initialNextAction || prev.nextAction,
      // Clear date/time/note for reschedule so user enters new values
      ...(rescheduleMode ? {
        interactionDate: "",
        interactionTime: "",
        interactionNote: "",
        googleMapsUrl: "",
      } : {}),
    }));
  }, [isOpen, initialInteractionType, initialNextAction, rescheduleMode]);

  const hasFormData = () => {
    // Don't include contactMethod as it has a default value
    return !!(
      formData.interactionType ||
      formData.interactionNote ||
      formData.nextAction ||
      formData.interactionDate ||
      formData.interactionTime
    );
  };

  // const isInteractionValid = (): boolean => {
  //   if (!formData.interactionType) {
  //     return false;
  //   }

  //   if (formData.interactionType === "postUpdate") {
  //     if (!formData.contactMethod) {
  //       return false;
  //     }
  //     if (!formData.interactionNote.trim()) {
  //       return false;
  //     }
  //     if (!formData.nextAction) {
  //       return false;
  //     }
  //     if (
  //       formData.nextAction === "callLater" &&
  //       (!formData.interactionDate.trim() || !formData.interactionTime.trim())
  //     ) {
  //       return false;
  //     }
  //   }

  //   if (
  //     formData.interactionType === "storeVisit" ||
  //     formData.interactionType === "siteVisit"
  //   ) {
  //     if (
  //       !formData.interactionDate.trim() ||
  //       !formData.interactionTime.trim()
  //     ) {
  //       return false;
  //     }
  //     if (!formData.interactionNote.trim()) {
  //       return false;
  //     }
  //   }

  //   return true;
  // };

  // Pure validation function (doesn't set state) - for use during render
  // const isFormValid = (): boolean => {
  //   if (!isInteractionValid()) {
  //     return false;
  //   }

  //   return true;
  // };

  // Validation function that sets errors - for use on submit
  // const validateForm = (): boolean => {
  //   const newErrors: FormErrors = {};

  //   if (!formData.interactionType) {
  //     newErrors.interactionType = "Interaction type is required";
  //   } else if (formData.interactionType === "postUpdate") {
  //     if (!formData.contactMethod) {
  //       newErrors.contactMethod = "Contact method is required";
  //     }
  //     if (!formData.interactionNote.trim()) {
  //       newErrors.interactionNote = "Note is required";
  //     }
  //     if (!formData.nextAction) {
  //       newErrors.nextAction = "Next action is required";
  //     }
  //     if (
  //       formData.nextAction === "callLater" &&
  //       (!formData.interactionDate.trim() || !formData.interactionTime.trim())
  //     ) {
  //       if (!formData.interactionDate.trim()) {
  //         newErrors.interactionDate = "Date is required";
  //       }
  //       if (!formData.interactionTime.trim()) {
  //         newErrors.interactionTime = "Time is required";
  //       }
  //     }
  //   } else if (
  //     formData.interactionType === "storeVisit" ||
  //     formData.interactionType === "siteVisit"
  //   ) {
  //     if (
  //       !formData.interactionDate.trim() ||
  //       !formData.interactionTime.trim()
  //     ) {
  //       if (!formData.interactionDate.trim()) {
  //         newErrors.interactionDate = "Date is required";
  //       }
  //       if (!formData.interactionTime.trim()) {
  //         newErrors.interactionTime = "Time is required";
  //       }
  //     }
  //     if (!formData.interactionNote.trim()) {
  //       newErrors.interactionNote = "Note is required";
  //     }
  //   }

  //   // Disallow past dates (today and future only)
  //   if (formData.interactionDate && formData.interactionDate < minDate) {
  //     newErrors.interactionDate = "Date must be today or a future date";
  //   }

  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      const type = "type" in e.target ? e.target.type : "text";
      const checked = "checked" in e.target ? e.target.checked : false;

      // Update form data
      setFormData((prev) => {
        const newData = {
          ...prev,
          [name]: type === "checkbox" ? checked : value,
        };
        return newData;
      });

      // Clear error when user starts typing (only if error exists to avoid unnecessary updates)
      setErrors((prev) => {
        const errorKey = name as keyof FormErrors;
        // Only update if the error actually exists
        if (prev[errorKey]) {
          return {
            ...prev,
            [errorKey]: undefined,
          };
        }
        return prev;
      });
    },
    [],
  );

  const handleClose = () => {
    if (hasFormData()) {
      setIsConfirmDialogOpen(true);
    } else {
      onRequestClose();
    }
  };

  const handleConfirmClose = () => {
    setIsConfirmDialogOpen(false);
    // Reset form
    setFormData({
      type: "individual",
      organizerName: "",
      businessName: "",
      designation: "",
      decisionMaker: "",
      name: "",
      email: "",
      mobile: "",
      interactionType: "",
      contactMethod: "phone",
      interactionNote: "",
      nextAction: "",
      interactionDate: "",
      interactionTime: "",
      googleMapsUrl: "",
    });
    setErrors({});
    setIsCallLaterTimePickerOpen(false);
    setIsVisitTimePickerOpen(false);
    onRequestClose();
  };

  const handleCancelClose = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleRequestClose = () => {
    handleClose();
  };

  // Helper function to check if a specific stage/action is completed
  // This is the single source of truth for checking if an action is completed
  const getCompletedStagesSet = useMemo(() => {
    const completedSet = new Set<DetailedStage>();

    // Optional stages (storeVisit, siteVisit, designFinalized) are flexible and can be done in any order
    // We should NOT use stage order to determine if they're completed - only explicit completion indicators
    const optionalStages: DetailedStage[] = ['storeVisit', 'siteVisit', 'designFinalized'];
    const fixedStages: DetailedStage[] = ['new', 'contacted', 'followup', 'measurement', 'quotation', 'won', 'lost', 'hold'];

    // For fixed stages, use the hook to determine completion based on order and sub-status
    allDynamicStages.forEach(stage => {
      // Only use hook status for fixed stages (not optional stages)
      if (!isOptional(stage.key)) {
        const status = getHookStageStatus(stage.key, currentStage, currentStageSubStatus);
        if (status === 'completed') {
          completedSet.add(stage.key);
        }
      }
    });

    // For optional stages, ONLY use explicit completion indicators (not stage order)
    // This is the PRIMARY source of truth for optional stages
    if (leadCompletedStages) {
      // Check each tracked stage - if it's marked as completed, add it to the set
      if (leadCompletedStages.storeVisit === 'completed') {
        completedSet.add('storeVisit');
      }
      if (leadCompletedStages.siteVisit === 'completed') {
        completedSet.add('siteVisit');
      }
      if (leadCompletedStages.designFinalized) {
        // designFinalized doesn't have sub-status, if it exists in completedStages, it's completed
        completedSet.add('designFinalized');
      }
      if (leadCompletedStages.measurement === 'completed') {
        completedSet.add('measurement');
      }
      // Quotation is completed when it's approved (not just sent)
      if (leadCompletedStages.quotation === 'approved') {
        completedSet.add('quotation');
      }
    }

    // Additional check: Look through follow-up history to find completed stages
    // This is a secondary check to catch any stages that might have been completed
    // but not yet reflected in leadCompletedStages
    if (followUpHistory && followUpHistory.length > 0) {
      for (const activity of followUpHistory) {
        const channel = (activity.channel || '').toLowerCase();
        const channelOriginal = activity.channel || '';
        const outcome = (activity.outcome || '').toLowerCase();
        const outcomeOriginal = activity.outcome || '';

        // Check for store visit completion
        // Completed if: 
        // 1. type is storeVisit AND (outcome is "Completed" in any case OR outcome includes "completed"), OR
        // 2. channel explicitly says "Store Visit Completed" (any case), OR
        // 3. channel includes "store visit" AND "completed" (case-insensitive)
        if (activity.type === 'storeVisit') {
          // Check outcome field (case-insensitive)
          const hasCompletedOutcome = outcomeOriginal && (
            outcome === 'completed' ||
            outcomeOriginal === 'Completed' ||
            outcomeOriginal.toLowerCase() === 'completed' ||
            (outcome && outcome.length > 0 && outcome.includes('completed'))
          );

          // Check channel field
          const hasCompletedChannel = channelOriginal && (
            channelOriginal === 'Store Visit Completed' ||
            channelOriginal === 'store visit completed' ||
            channelOriginal.toLowerCase() === 'store visit completed' ||
            (channel.includes('store visit') && channel.includes('completed'))
          );

          if (hasCompletedOutcome || hasCompletedChannel) {
            completedSet.add('storeVisit');
            // Debug log
            if (process.env.NODE_ENV === 'development') {
              console.log('Store Visit marked as completed:', {
                type: activity.type,
                channel: activity.channel,
                outcome: activity.outcome,
                hasCompletedOutcome,
                hasCompletedChannel
              });
            }
          }
        }

        // Check for site visit completion
        // Completed if:
        // 1. type is siteVisit AND (outcome is "Completed" in any case OR outcome includes "completed"), OR
        // 2. channel explicitly says "Site Visit Completed" (any case), OR
        // 3. channel includes "site visit" AND "completed" (case-insensitive)
        if (activity.type === 'siteVisit') {
          const hasCompletedOutcome = outcomeOriginal && (
            outcome === 'completed' ||
            outcomeOriginal === 'Completed' ||
            (outcome && outcome.includes('completed'))
          );
          const hasCompletedChannel = channelOriginal && (
            channelOriginal === 'Site Visit Completed' ||
            channelOriginal === 'site visit completed' ||
            (channel.includes('site visit') && channel.includes('completed'))
          );

          if (hasCompletedOutcome || hasCompletedChannel) {
            completedSet.add('siteVisit');
          }
        }

        // Also check for explicit "Completed" channels regardless of type
        // This handles cases where completion is logged as a separate activity
        if (channelOriginal === 'Store Visit Completed' ||
          channelOriginal === 'store visit completed' ||
          (channel.includes('store visit') && channel.includes('completed'))) {
          completedSet.add('storeVisit');
        }
        if (channelOriginal === 'Site Visit Completed' ||
          channelOriginal === 'site visit completed' ||
          (channel.includes('site visit') && channel.includes('completed'))) {
          completedSet.add('siteVisit');
        }

        // Check for design finalized
        // Completed if channel mentions "Design Finalized" or "Design Final"
        if (channel.includes('design') && (channel.includes('finalized') || channel.includes('final'))) {
          completedSet.add('designFinalized');
        }

        // Check for measurement completion
        // Completed if channel mentions "Measurement Done" or "Measurement Completed"
        if (channel.includes('measurement') && (channel.includes('done') || channel.includes('completed'))) {
          completedSet.add('measurement');
        }

        // Check for quotation approved
        // Completed if channel mentions "Quotation Approved" or outcome is "Approved"
        if ((channel.includes('quotation') && channel.includes('approved')) ||
          (channel.includes('quotation') && outcome === 'approved')) {
          completedSet.add('quotation');
        }
      }
    }

    // For optional stages, also check if current stage is the optional stage and it's completed
    // This handles the case where we're currently at an optional stage that's been completed
    optionalStages.forEach(optStage => {
      if (currentStage === optStage) {
        if (optStage === 'designFinalized') {
          // designFinalized is always considered completed when it's the current stage
          completedSet.add(optStage);
        } else if (currentStageSubStatus === 'completed') {
          // For storeVisit and siteVisit, check sub-status
          completedSet.add(optStage);
        }
      }
    });

    // Debug logging (can be removed after verification)
    if (process.env.NODE_ENV === 'development') {
      console.log('Completion Detection Debug:', {
        storeVisitCompleted: completedSet.has('storeVisit'),
        siteVisitCompleted: completedSet.has('siteVisit'),
        designFinalizedCompleted: completedSet.has('designFinalized'),
        followUpHistory: followUpHistory?.map(a => ({
          type: a.type,
          channel: a.channel,
          outcome: a.outcome
        })),
        leadCompletedStages,
        currentStage,
        currentStageSubStatus
      });
    }

    return completedSet;
  }, [currentStage, currentStageSubStatus, followUpHistory, leadCompletedStages, allDynamicStages, getHookStageStatus, isOptional]);

  // Helper functions to check if specific actions are completed (similar to isTerminalStageReached)
  const isStoreVisitCompleted = useMemo(() => {
    const isCompleted = getCompletedStagesSet.has('storeVisit');
    // Debug log
    if (process.env.NODE_ENV === 'development') {
      console.log('isStoreVisitCompleted check:', {
        isCompleted,
        completedStages: Array.from(getCompletedStagesSet),
        followUpHistory: followUpHistory?.map(a => ({
          type: a.type,
          channel: a.channel,
          outcome: a.outcome
        }))
      });
    }
    return isCompleted;
  }, [getCompletedStagesSet, followUpHistory]);

  const isSiteVisitCompleted = useMemo(() => {
    return getCompletedStagesSet.has('siteVisit');
  }, [getCompletedStagesSet]);

  const isDesignFinalizedCompleted = useMemo(() => {
    return getCompletedStagesSet.has('designFinalized');
  }, [getCompletedStagesSet]);

  const isQuotationCompleted = useMemo(() => {
    return getCompletedStagesSet.has('quotation');
  }, [getCompletedStagesSet]);

  const allowedNextActions: NextAction[] | null = useMemo(() => {
    if (!currentStage) {
      return null; // No restriction if stage is not provided
    }

    const completedSet = getCompletedStagesSet;

    // Helper function to get optional stage buttons (siteVisit, storeVisit, designFinalized)
    // These are flexible stages that can be completed in any order
    const getOptionalStageButtons = (): NextAction[] => {
      const result: NextAction[] = [];

      // Optional stages: only show if NOT already completed
      // These remain available even after moving to measurement/quotation
      if (!completedSet.has('siteVisit')) {
        result.push('siteVisit');
      }

      // Store visit: only show if NOT completed AND design is NOT finalized
      // If design is already finalized, no need to visit store for design selection
      if (!completedSet.has('storeVisit') && !completedSet.has('designFinalized')) {
        result.push('storeVisit');
      }

      if (!completedSet.has('designFinalized')) {
        result.push('designFinalized');
      }

      return result;
    };

    // Helper function to get compulsory buttons for early stages
    const getCompulsoryButtons = (): NextAction[] => {
      const result = getOptionalStageButtons();
      result.push('callLater', 'notInterested');
      return result;
    };

    // =====================================================================
    // Algorithm: Flexible stage workflow
    // Fixed stages: [new, contacted, followup] [measurement, quotation, won/lost/hold]
    // Flexible stages: [siteVisit, storeVisit, designFinalized] - can be done in any order
    // Store visit is optional and not required
    // =====================================================================

    // Check if we're at early stages (new, contacted, followup) - show all optional buttons
    if (currentStage === 'new' || currentStage === 'contacted' || currentStage === 'followup') {
      return getCompulsoryButtons();
    }

    // 1️⃣ If terminal stage already completed → show NOTHING (workflow locked)
    if (completedSet.has('won') || completedSet.has('lost') || completedSet.has('hold')) {
      return [];
    }

    // 2️⃣ If quotation completed → show [won, lost, hold]
    if (completedSet.has('quotation')) {
      return ['won', 'lost', 'hold', 'callLater', 'notInterested'];
    }

    // 3️⃣ If measurement completed → show [quotation] only (hide optional stages: storeVisit, siteVisit, designFinalized)
    if (completedSet.has('measurement')) {
      const result: NextAction[] = [];

      // After measurement is completed, don't show optional stages (storeVisit, siteVisit, designFinalized)
      // Only show quotation and standard actions

      if (!completedSet.has('quotation')) {
        result.push('quotationSent');
      }
      result.push('callLater', 'notInterested');

      // Add completion sub-actions if applicable to current stage
      if (currentStage === 'quotation' && currentStageSubStatus === 'send') {
        result.push('quotationApproved');
      }

      return result;
    }

    // 4️⃣ If followUp completed OR we're at any stage past followup (but BEFORE measurement) 
    // → show all optional NOT completed + measurement
    // After measurement is completed, optional stages are hidden (handled in section 3)
    if (completedSet.has('followup') ||
      currentStage === 'siteVisit' ||
      currentStage === 'storeVisit' ||
      currentStage === 'designFinalized') {
      const result: NextAction[] = [];

      // Optional stages: show if NOT already completed (only before measurement)
      result.push(...getOptionalStageButtons());

      // Measurement is always allowed after followUp (doesn't require optional stages)
      if (!completedSet.has('measurement')) {
        result.push('measurementDone');
      }

      // Always include callLater and notInterested
      result.push('callLater', 'notInterested');

      // Add completion sub-actions if applicable to current stage
      if (currentStage === 'storeVisit' && currentStageSubStatus === 'scheduled') {
        result.push('storeVisitCompleted');
      }
      if (currentStage === 'siteVisit' && currentStageSubStatus === 'scheduled') {
        result.push('siteVisitCompleted');
      }

      return result;
    }

    // 5️⃣ If we're at measurement or quotation stage (but measurement not completed yet)
    // → show measurement if not completed, but don't show optional stages
    if (currentStage === 'measurement' || currentStage === 'quotation') {
      const result: NextAction[] = [];

      // Don't show optional stages at measurement/quotation stage
      // Only show measurement if not completed, and quotation if measurement is done
      if (!completedSet.has('measurement')) {
        result.push('measurementDone');
      }

      if (completedSet.has('measurement') && !completedSet.has('quotation')) {
        result.push('quotationSent');
      }

      result.push('callLater', 'notInterested');

      // Add completion sub-actions if applicable to current stage
      if (currentStage === 'quotation' && currentStageSubStatus === 'send') {
        result.push('quotationApproved');
      }

      return result;
    }

    // Default: if no stages completed yet, show basic actions
    return ['callLater', 'notInterested'];
  }, [currentStage, currentStageSubStatus, followUpHistory, getCompletedStagesSet]);

  const isNextActionAllowed = (action: NextAction): boolean => {
    if (!allowedNextActions) return true; // Show all when we don't know the stage

    // Additional safeguard: Don't allow storeVisit/siteVisit if they're completed
    if (action === 'storeVisit' && isStoreVisitCompleted) {
      return false;
    }
    if (action === 'siteVisit' && isSiteVisitCompleted) {
      return false;
    }

    // Don't show store visit if design is already finalized
    // (no need to visit store if design selection is already done)
    if (action === 'storeVisit' && isDesignFinalizedCompleted) {
      return false;
    }

    return allowedNextActions.includes(action);
  };

  // Check if terminal stage is reached - disable all actions except callLater
  // This is now handled in allowedNextActions, but kept for backward compatibility
  const isTerminalStageReached = useMemo(() => {
    if (!currentStage) return false;
    return isTerminal(currentStage);
  }, [currentStage, isTerminal]);

  const InteractionSection = useMemo(() => {
    const isStoreVisitAction = formData.nextAction === "storeVisit";
    const isSiteVisitAction = formData.nextAction === "siteVisit";
    const isNotInterestedAction = formData.nextAction === "notInterested";
    const isMeasurementDoneAction = formData.nextAction === "measurementDone";

    const shouldShowPostUpdateDateTime =
      (formData.nextAction === "callLater" ||
        (formData.interactionType === "postUpdate" && formData.contactMethod === "phone" && !formData.nextAction)) &&
      !isNotInterestedAction;

    // In reschedule mode, show simplified UI with just date/time/note fields
    if (rescheduleMode) {
      return (
        <div className="space-y-4 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <DocumentTextIcon className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">
              {initialNextAction === 'storeVisit' ? 'Store Visit Details' : 'Site Visit Details'}
            </h3>
          </div>

          {/* Date & Time for reschedule */}
          <div className="space-y-4">
            <div className="mb-2 grid grid-cols-2 gap-4">
              <TextInputField
                label="Date"
                name="interactionDate"
                type="date"
                value={formData.interactionDate}
                onChange={handleChange}
                required
                min={minDate}
                inputRef={dateInputRef}
                error={!!errors.interactionDate}
                helperText={errors.interactionDate}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Time"
                  value={timeStringToDate(formData.interactionTime)}
                  onOpen={() => setIsVisitTimePickerOpen(true)}
                  onClose={() => setIsVisitTimePickerOpen(false)}
                  onChange={(newValue) => {
                    handleInteractionTimeChange(newValue, () => setIsVisitTimePickerOpen(false));
                  }}
                  minTime={minTime}
                  maxTime={maxTime}
                  skipDisabled
                  minutesStep={TIME_MINUTE_STEP}
                  shouldDisableTime={(value, view) => {
                    if (view !== "minutes") return false;
                    const date = toDateValue(value);
                    return !!date && date.getMinutes() % TIME_MINUTE_STEP !== 0;
                  }}
                  slotProps={{
                    popper: {
                      sx: { zIndex: 13000 },
                    },
                    textField: {
                      fullWidth: true,
                      error: !!errors.interactionTime,
                      helperText: errors.interactionTime,
                      variant: "outlined" as const,
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            {/* Google Maps URL - Only for Site Visit */}
            {initialNextAction === 'siteVisit' && (
              <div className="mb-2">
                <TextField
                  fullWidth
                  label="Google Maps Address URL"
                  name="googleMapsUrl"
                  type="url"
                  value={formData.googleMapsUrl}
                  onChange={handleChange}
                  placeholder="https://maps.google.com/..."
                  helperText="Paste the Google Maps link for the site location"
                  variant="outlined"
                />
              </div>
            )}

            {/* Note/Summary */}
            <div className="mb-2">
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Note"
                required={true}
                name="interactionNote"
                value={formData.interactionNote}
                onChange={handleChange}
                error={!!errors.interactionNote}
                helperText={errors.interactionNote || "Add any notes about the rescheduled visit"}
                variant="outlined"
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <DocumentTextIcon className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">
            Interaction Details
          </h3>
        </div>

        {/* General Interaction Fields (includes Next Action, Call Later, Store/Site Visit etc.) */}
        <div className="space-y-4">
          {/* Contact Method */}
          <div className="mb-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-700">
                Contact Method
              </span>
            </div>

            <div className="flex gap-3">
              {CONTACT_METHODS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      contactMethod: value,
                      // Auto-set interactionType to postUpdate when contact method is selected
                      interactionType: prev.interactionType || "postUpdate",
                    }))
                  }
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.contactMethod === value
                    ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {errors.contactMethod && (
              <span className="text-red-500 text-xs block mt-1">
                {errors.contactMethod}
              </span>
            )}
          </div>

          {/* Next Action + Stage buttons (same section) */}
          <div className="mb-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-700">
                Next Action
              </span>
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3">
                {isNextActionAllowed("callLater") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "callLater",
                        interactionType: "postUpdate",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "callLater"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Call Later
                  </button>
                )}
                {isNextActionAllowed("notInterested") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "notInterested",
                        interactionType: "postUpdate",
                        interactionDate: "",
                        interactionTime: "",
                        googleMapsUrl: "",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "notInterested"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Not Interested
                  </button>
                )}
              </div>
              {errors.nextAction && (
                <span className="text-red-500 text-xs block mt-1">
                  {errors.nextAction}
                </span>
              )}

              {/* Stage / Status buttons (inline with Next Action, uniform styles) */}
              <div className="flex flex-wrap gap-3">
                {/* Row 1: Visit / Design */}
                {!isTerminalStageReached && !isStoreVisitCompleted && isNextActionAllowed("storeVisit") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "storeVisit",
                        interactionType: "storeVisit",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "storeVisit"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Store Visit
                  </button>
                )}
                {!isTerminalStageReached && !isStoreVisitCompleted && isNextActionAllowed("storeVisitCompleted") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "storeVisitCompleted",
                        interactionType: "postUpdate",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "storeVisitCompleted"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Mark Store Completed
                  </button>
                )}
                {!isTerminalStageReached && !isSiteVisitCompleted && isNextActionAllowed("siteVisit") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "siteVisit",
                        interactionType: "siteVisit",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "siteVisit"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Site Visit
                  </button>
                )}
                {!isTerminalStageReached && !isSiteVisitCompleted && isNextActionAllowed("siteVisitCompleted") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "siteVisitCompleted",
                        interactionType: "postUpdate",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "siteVisitCompleted"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Mark Site Completed
                  </button>
                )}
                {!isTerminalStageReached && isNextActionAllowed("designFinalized") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "designFinalized",
                        interactionType: "postUpdate",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "designFinalized"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Design Finalized
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                {/* Row 2: Measurement / Quotation */}
                {!isTerminalStageReached && isNextActionAllowed("measurementDone") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "measurementDone",
                        interactionType: "siteVisit",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "measurementDone"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Measurement Done
                  </button>
                )}
                {!isTerminalStageReached && !isQuotationCompleted && isNextActionAllowed("quotationSent") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "quotationSent",
                        interactionType: "postUpdate",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "quotationSent"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Quotation Sent
                  </button>
                )}
                {!isTerminalStageReached && !isQuotationCompleted && isNextActionAllowed("quotationApproved") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "quotationApproved",
                        interactionType: "postUpdate",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "quotationApproved"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Quotation Approved
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                {/* Row 3: Final status */}
                {!isTerminalStageReached && isNextActionAllowed("won") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "won",
                        interactionType: "postUpdate",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "won"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Won
                  </button>
                )}
                {!isTerminalStageReached && isNextActionAllowed("lost") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "lost",
                        interactionType: "postUpdate",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "lost"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Lost
                  </button>
                )}
                {!isTerminalStageReached && isNextActionAllowed("hold") && (
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        nextAction: "hold",
                        interactionDate: "",
                        interactionTime: "",
                        googleMapsUrl: "",
                        interactionType: "postUpdate",
                      }))
                    }
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${formData.nextAction === "hold"
                      ? "bg-primary-600 text-white border-primary-600 hover:bg-primary-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    Hold
                  </button>
                )}
              </div>

              {/* Quotation attachment field */}
              {formData.nextAction === "quotationSent" && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attach File
                  </label>
                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setQuotationFile(file);
                    }}
                    className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-medium file:bg-white file:text-gray-700 hover:file:bg-gray-50"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Common Date, Time, and Note Fields - Shown conditionally based on selected action */}
          {(shouldShowPostUpdateDateTime || isStoreVisitAction || isSiteVisitAction || isMeasurementDoneAction || (formData.contactMethod === "phone" && formData.interactionType === "postUpdate")) && !isNotInterestedAction && (
            <div className="space-y-4">
              {/* Date & Time - Show for callLater, storeVisit, siteVisit, measurementDone, or phone calls */}
              {(shouldShowPostUpdateDateTime || isStoreVisitAction || isSiteVisitAction || isMeasurementDoneAction || (formData.contactMethod === "phone" && formData.interactionType === "postUpdate")) && !isNotInterestedAction && (
                <div className="mb-2 grid grid-cols-2 gap-4">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Date"
                      value={dateStringToDate(formData.interactionDate)}
                      minDate={dateStringToDate(minDate)}
                      onChange={(newValue) => {
                        const dateStr = dateToDateString(newValue);
                        setFormData((prev) => ({
                          ...prev,
                          interactionDate: dateStr,
                        }));
                        if (errors.interactionDate) {
                          setErrors((prev) => ({
                            ...prev,
                            interactionDate: undefined,
                          }));
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          inputRef: dateInputRef,
                          error: !!errors.interactionDate,
                          helperText: errors.interactionDate,
                          variant: "outlined" as const,
                        },
                        popper: {
                          sx: { zIndex: 13000 },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      label="Time"
                      value={timeStringToDate(formData.interactionTime)}
                      ampm={false}
                      format="HH:mm"
                      minTime={minTime}
                      maxTime={maxTime}
                      skipDisabled
                      minutesStep={TIME_MINUTE_STEP}
                      shouldDisableTime={(value, view) => {
                        if (view !== "minutes") return false;
                        const date = toDateValue(value);
                        return !!date && date.getMinutes() % TIME_MINUTE_STEP !== 0;
                      }}
                      // open={shouldShowPostUpdateDateTime ? isCallLaterTimePickerOpen : isVisitTimePickerOpen}
                      onOpen={() => {
                        if (shouldShowPostUpdateDateTime) {
                          setIsCallLaterTimePickerOpen(true);
                        } else {
                          setIsVisitTimePickerOpen(true);
                        }
                      }}
                      onClose={() => {
                        if (shouldShowPostUpdateDateTime) {
                          setIsCallLaterTimePickerOpen(false);
                        } else {
                          setIsVisitTimePickerOpen(false);
                        }
                      }}
                      onChange={(newValue) => {
                        if (shouldShowPostUpdateDateTime) {
                          handleInteractionTimeChange(newValue, () => setIsCallLaterTimePickerOpen(false));
                        } else {
                          handleInteractionTimeChange(newValue, () => setIsVisitTimePickerOpen(false));
                        }
                      }}
                      onError={(reason, value) => {
                        if (!value) return;
                      
                        if (reason === "minTime" || reason === "maxTime") {
                          setErrors((prev) => ({
                            ...prev,
                            interactionTime: "Time must be between 07:00 and 21:00",
                          }));
                        }
                      }}
                      slotProps={{
                        popper: {
                          sx: { zIndex: 13000 },
                        },
                        textField: {
                          fullWidth: true,
                          // required: true,
                          error: !!errors.interactionTime,
                          helperText: errors.interactionTime,
                          variant: "outlined" as const,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              )}

              {/* Google Maps URL - Only for Site Visit */}
              {isSiteVisitAction && (
                <div className="mb-2">
                  <TextField
                    fullWidth
                    label="Google Maps Address URL"
                    name="googleMapsUrl"
                    type="url"
                    value={formData.googleMapsUrl}
                    onChange={handleChange}
                    placeholder="https://maps.google.com/..."
                    error={!!errors.googleMapsUrl}
                    variant="outlined"
                  />
                </div>
              )}
            </div>
          )}

          {/* Note - Show for all action types or when contact method is selected */}
          {(formData.nextAction || formData.contactMethod) && (
            <div className="mb-2">
              <TextField
                fullWidth
                label="Note"
                name="interactionNote"
                value={formData.interactionNote}
                onChange={handleChange}
                // required={isStoreVisitAction || isSiteVisitAction}
                error={!!errors.interactionNote}
                helperText={errors.interactionNote}
                multiline
                required={true}
                rows={4}
                variant="outlined"
              />
            </div>
          )}
        </div>
      </div>
    );
  }, [formData, errors, handleChange, isTerminalStageReached, isStoreVisitCompleted, isSiteVisitCompleted, isQuotationCompleted, isNextActionAllowed, rescheduleMode, initialNextAction, minDate, timeStringToDate, dateToTimeString]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // User requested to remove validation from Create Contact button,
    // so we no longer block submit based on validateForm().
    setIsSubmitting(true);

    try {
      // If leadId is provided, create a follow-up entry and optional stage update
      if (leadId && onSuccess) {
        const now = new Date();
        const dateStr = now.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        const timeStr = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        // Determine activity type and base channel
        let activityType:
          | "phone"
          | "whatsapp"
          | "email"
          | "siteVisit"
          | "storeVisit"
          | "followup" = "phone";
        let channel = "Phone Call";

        if (formData.interactionType === "siteVisit") {
          activityType = "siteVisit";
          channel = "Site Visit";
        } else if (formData.interactionType === "storeVisit") {
          activityType = "storeVisit";
          channel = "Store Visit";
        } else if (formData.interactionType === "postUpdate" || formData.contactMethod) {
          // Handle postUpdate or direct contact method selection
          if (formData.contactMethod === "phone") {
            activityType = "phone";
            channel = formData.nextAction ? "Follow-up Call" : "Phone Call";
          } else if (formData.contactMethod === "whatsapp") {
            activityType = "whatsapp";
            channel = formData.nextAction ? "Follow-up WhatsApp" : "WhatsApp";
          } else if (formData.contactMethod === "mail") {
            activityType = "email";
            channel = formData.nextAction ? "Follow-up Email" : "Email";
          } else {
            activityType = "followup";
            channel = "Follow-up";
          }
        }

        // Build summary and date string
        let summary = formData.interactionNote || "";
        let displayDate = `${dateStr}, ${timeStr}`;

        // Handle date/time for visits, callLater, or phone calls with date/time
        if (
          formData.interactionType === "siteVisit" ||
          formData.interactionType === "storeVisit" ||
          formData.nextAction === "storeVisit" ||
          formData.nextAction === "siteVisit" ||
          rescheduleMode
        ) {
          if (formData.interactionDate) {
            // Combine date and time for display
            const selectedDate = new Date(formData.interactionDate);
            const dateStrFormatted = selectedDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            });

            if (formData.interactionTime) {
              const [hours, minutes] = formData.interactionTime.split(":");
              selectedDate.setHours(parseInt(hours), parseInt(minutes));
              const timeStrFormatted = selectedDate.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              });
              displayDate = `${dateStrFormatted}, ${timeStrFormatted}`;
            } else {
              // Show N/A for time when not provided
              displayDate = `${dateStrFormatted}, N/A`;
            }
            summary = `${channel} on ${dateStrFormatted}. ${summary}`;
          }
        } else if (
          (formData.nextAction === "callLater" ||
            (formData.contactMethod === "phone" && formData.interactionDate && formData.interactionTime)) &&
          formData.interactionDate &&
          formData.interactionTime
        ) {
          // For scheduled follow-ups or phone calls with date/time, use the selected date and time
          const selectedDate = new Date(formData.interactionDate);
          const [hours, minutes] = formData.interactionTime.split(":");
          selectedDate.setHours(parseInt(hours), parseInt(minutes));

          const dateStrFormatted = selectedDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          const timeStrFormatted = selectedDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          displayDate = `${dateStrFormatted}, ${timeStrFormatted}`;
        }

        // Override channel for certain next actions so the timeline header
        // clearly reflects the stage / action selected by the user.
        switch (formData.nextAction) {
          case "storeVisit":
            activityType = "storeVisit";
            channel = rescheduleMode ? "Store Visit Rescheduled" : "Store Visit";
            break;
          case "storeVisitCompleted":
            activityType = "storeVisit";
            channel = "Store Visit Completed";
            break;
          case "siteVisit":
            activityType = "siteVisit";
            channel = rescheduleMode ? "Site Visit Rescheduled" : "Site Visit";
            break;
          case "siteVisitCompleted":
            activityType = "siteVisit";
            channel = "Site Visit Completed";
            break;
          case "designFinalized":
            channel = "Design Finalized";
            break;
          case "measurementDone":
            // Part of site visit flow but show a clearer label in the timeline
            channel = "Measurement Done";
            break;
          case "quotationSent":
            channel = "Quotation Sent";
            break;
          case "quotationApproved":
            channel = "Quotation Approved";
            break;
          case "won":
            channel = "Won";
            break;
          case "lost":
            channel = "Lost";
            break;
          case "hold":
            channel = "On Hold";
            break;
          case "notInterested":
            channel = "Not Interested";
            break;
          default:
            // For follow-up style actions like "Call Later",
            // keep the "Follow-up Call/WhatsApp/Mail" style channel above.
            break;
        }

        // Determine outcome
        let outcome = "Completed";
        let outcomeColor: "success" | "info" | "warning" | "error" = "success";

        if (rescheduleMode) {
          outcome = "Rescheduled";
          outcomeColor = "info";
        } else if (formData.nextAction === "callLater") {
          outcome = "Follow-up Scheduled";
          outcomeColor = "info";
        } else if (formData.nextAction === "notInterested") {
          outcome = "Not Interested - Lead Lost";
          outcomeColor = "error";
        } else if (formData.nextAction === "storeVisit" || formData.nextAction === "siteVisit") {
          outcome = "Scheduled";
          outcomeColor = "info";
        }

        // Get owner info (you can get this from user context or use a default)
        const owner = "Current User"; // TODO: Get from user context
        const ownerInitials = owner
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2);

        // Build a proper ISO date for the `date` field.
        // displayDate is a human-readable string – but the timeline
        // renders via `new Date(activity.date)`, so we need a parseable ISO string.
        let isoDate: string;

        // Debug logging for date handling
        console.log('handleSubmit - Date handling:', {
          rescheduleMode,
          interactionType: formData.interactionType,
          nextAction: formData.nextAction,
          interactionDate: formData.interactionDate,
          interactionTime: formData.interactionTime,
          hasDate: !!formData.interactionDate,
          hasTime: !!formData.interactionTime,
          fullFormData: formData,
        });

        // Check if we should use the selected date
        const shouldUseSelectedDate =
          formData.interactionType === "siteVisit" ||
          formData.interactionType === "storeVisit" ||
          formData.nextAction === "callLater" ||
          formData.nextAction === "storeVisit" ||
          formData.nextAction === "siteVisit" ||
          rescheduleMode ||
          (formData.contactMethod === "phone" && formData.interactionDate);

        if (shouldUseSelectedDate && formData.interactionDate) {
          const selectedDate = new Date(formData.interactionDate);
          // Use provided time if available, otherwise keep date only (time will show as N/A)
          if (formData.interactionTime) {
            const [hours, minutes] = formData.interactionTime.split(":");
            selectedDate.setHours(parseInt(hours), parseInt(minutes));
          } else {
            // Set to start of day when no time provided
            selectedDate.setHours(0, 0, 0, 0);
          }
          isoDate = selectedDate.toISOString();
          console.log('Using selected date:', isoDate);
        } else {
          isoDate = now.toISOString();
          console.log('Using current date (now):', isoDate, 'Reason: shouldUseSelectedDate=', shouldUseSelectedDate, 'hasDate=', !!formData.interactionDate);
        }

        const followUpEntry = {
          id: `followup-${Date.now()}`,
          type: activityType,
          channel,
          date: isoDate,
          summary,
          outcome,
          outcomeColor,
          owner,
          ownerInitials,
          createdAt: now.toISOString(),
        };
        let stageUpdate:
          | {
            detailedStage: DetailedStage;
            stageSubStatus?: "scheduled" | "completed" | "send" | "approved";
          }
          | undefined;

        switch (formData.nextAction) {
          case "storeVisit":
            stageUpdate = {
              detailedStage: "storeVisit",
              stageSubStatus: "scheduled",
            };
            break;
          case "storeVisitCompleted":
            stageUpdate = {
              detailedStage: "storeVisit",
              stageSubStatus: "completed",
            };
            break;
          case "siteVisit":
            stageUpdate = {
              detailedStage: "siteVisit",
              stageSubStatus: "scheduled",
            };
            break;
          case "siteVisitCompleted":
            stageUpdate = {
              detailedStage: "siteVisit",
              stageSubStatus: "completed",
            };
            break;
          case "designFinalized":
            stageUpdate = { detailedStage: "designFinalized" };
            break;
          case "measurementDone":
            stageUpdate = {
              detailedStage: "measurement",
              stageSubStatus: "completed",
            };
            break;
          case "quotationSent":
            stageUpdate = {
              detailedStage: "quotation",
              stageSubStatus: "send",
            };
            break;
          case "quotationApproved":
            stageUpdate = {
              detailedStage: "quotation",
              stageSubStatus: "approved",
            };
            break;
          case "won":
            stageUpdate = { detailedStage: "won" };
            break;
          case "lost":
            stageUpdate = { detailedStage: "lost" };
            break;
          case "hold":
            stageUpdate = { detailedStage: "hold" };
            break;
          case "notInterested":
            stageUpdate = { detailedStage: "lost" };
            break;
          default:
            stageUpdate = undefined;
        }

        onSuccess({
          followUpEntry,
          stageUpdate,
        });
      }

      // Reset form
      setFormData({
        type: "individual",
        organizerName: "",
        businessName: "",
        designation: "",
        decisionMaker: "",
        name: "",
        email: "",
        mobile: "",
        interactionType: "",
        contactMethod: "phone",
        interactionNote: "",
        nextAction: "",
        interactionDate: "",
        interactionTime: "",
        googleMapsUrl: "",
      });
      setErrors({});
      setIsCallLaterTimePickerOpen(false);
      setIsVisitTimePickerOpen(false);
      onRequestClose();
    } catch (error) {
      console.error("Error creating contact:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={() => handleRequestClose()}
      ></div>

      {/* Slide-in Panel */}
      <div
        className={`absolute right-0 top-0 h-full w-[30%] bg-white shadow-xl transform transition-transform duration-1000 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {rescheduleMode && initialNextAction === 'storeVisit'
                ? 'Reschedule Store Visit'
                : rescheduleMode && initialNextAction === 'siteVisit'
                  ? 'Reschedule Site Visit'
                  : 'Log Activity'}
            </h2>
            <button
              onClick={() => handleRequestClose()}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              type="button"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Interaction Section */}
              {InteractionSection}

              {/* Submit Error Message */}
              {errors.submit && (
                <p className="text-sm text-red-500 dark:text-red-400 mt-2">
                  {errors.submit}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-between gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => handleRequestClose()}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`inline-flex items-center px-4 py-2 shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    // "Create Contact"
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmText="Yes, Close"
        cancelText="Cancel"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </div>
  );
};

export default CreateContactModal;
